/** @jsx jsx */

import { useMemo, useState } from 'react';
import { Icon, Text } from '@zapier/design-system';
import { jsx } from '@emotion/core';

import { BaseTile } from './BaseTile';
import { CommonTimeSeriesChart } from 'app/developer-v3/components/CommonChart/CommonTimeSeriesChart';
import { generateYearsDropdownOptions } from '../utils';
import DateRangeDropdown from '../DateRangeDropdown';
import useQueryInsightsMonthlyActiveUsers from 'app/developer-v3/hooks/useQueryInsightsMonthlyActiveUsers';

interface MonthlyUsersTileProps {
  appId: string;
  dataTestId?: string;
  /** Color of the column in the chart */
  columnColor: string;
}

export default function MonthlyUsersTile({
  appId,
  dataTestId,
  columnColor,
}: MonthlyUsersTileProps) {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // by default the current year is selected
  const [hasRenderError, setHasRenderError] = useState(false);

  const {
    data: monthlyActiveUsersData,
    isError,
  } = useQueryInsightsMonthlyActiveUsers(appId);

  const xAxisLabel = 'Zap Used Month';
  const yAxisLabel = 'Count Activated Users';

  const dropdownItems = generateYearsDropdownOptions(monthlyActiveUsersData);

  // Filter chart data based on selected year
  const chartInfo = useMemo(() => {
    return monthlyActiveUsersData.reduce(
      (chartData, { activity_month_end_date, users_active_monthly }) => {
        if (new Date(activity_month_end_date).getFullYear() === selectedYear) {
          chartData.push({
            dateString: activity_month_end_date.toString(),
            value: users_active_monthly,
          });
        }
        return chartData;
      },
      []
    );
  }, [selectedYear, monthlyActiveUsersData]);

  return (
    <BaseTile
      dataTestId={dataTestId}
      dropdowns={[
        <DateRangeDropdown
          initialSelectedItem={dropdownItems.find(
            item => item.value === selectedYear
          )}
          items={dropdownItems}
          onSelect={setSelectedYear}
        />,
      ]}
      headerContent="Monthly active users"
      icon={<Icon name="personOne" size="24px" />}
      isError={isError || hasRenderError}
      // use the chart loader prop to handle display of loading state
      isLoading={false}
      isNoData={!(chartInfo && chartInfo.length > 0)}
      toolTipContent={
        <Text color="PrimeWhite" tag="p" type="Body1">
          Total number of users who had a Zap activated in a given month.
        </Text>
      }
      tooltipPosition="southeast"
    >
      <CommonTimeSeriesChart
        chartType="ColumnChart"
        colors={[columnColor]}
        data={chartInfo}
        hAxisFormat="MMM"
        setHasRenderError={setHasRenderError}
        xAxisLabel={xAxisLabel}
        yAxisLabel={yAxisLabel}
      />
    </BaseTile>
  );
}
